import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { EllipsisVertical, Check } from 'lucide-react';
import { Route as rootRoute } from './__root';
import { useAuthStore } from '../store/auth.store';
import { teamQueryOptions, type TeamMember } from '../lib/queries/team';
import { useUpdateMemberRole, useRemoveMember } from '../lib/mutations/team';
import Menus from '../components/Menus';
import { requireAdmin } from '../lib/guards';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  beforeLoad: requireAdmin,
  component: TeamPage,
});

const ROLE_LABEL: Record<TeamMember['role'], string> = {
  admin: 'Admin',
  support: 'Support',
  read_only: 'Read-only',
};

const ROLE_STYLE: Record<TeamMember['role'], string> = {
  admin: 'border-[--color-accent]/50 text-[--color-accent]',
  support: 'border-blue-500/50 text-blue-400',
  read_only: 'border-white/20 text-muted',
};

const STATUS_STYLE = {
  active: 'border-green-500/50 text-green-400',
  pending: 'border-yellow-500/50 text-yellow-400',
};

const STATUS_DOT: Record<string, string> = {
  active: 'bg-green-400',
  pending: 'bg-yellow-400',
  suspended: 'bg-red-400',
};

const PERMISSIONS = [
  { label: 'View tenants & usage', readOnly: true, support: true, admin: true },
  {
    label: 'View skills (read-only mirror)',
    readOnly: true,
    support: true,
    admin: true,
  },
  {
    label: 'Impersonate / debug session',
    readOnly: false,
    support: true,
    admin: true,
  },
  {
    label: 'Re-initiate OAuth flow',
    readOnly: false,
    support: true,
    admin: true,
  },
  {
    label: 'Rotate signing secrets',
    readOnly: false,
    support: false,
    admin: true,
  },
  {
    label: 'Change plan / billing',
    readOnly: false,
    support: false,
    admin: true,
  },
  {
    label: 'Onboard new tenants',
    readOnly: false,
    support: false,
    admin: true,
  },
  {
    label: 'Manage team & roles',
    readOnly: false,
    support: false,
    admin: true,
  },
];

function memberStatus(m: TeamMember): 'active' | 'pending' {
  return m.ssoIdentity?.startsWith('pending:') ? 'pending' : 'active';
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function ActionsMenu({
  id,
  member,
  onRoleChange,
  onRemove,
}: {
  id: string;
  member: TeamMember;
  onRoleChange: (role: TeamMember['role']) => void;
  onRemove: () => void;
}) {
  const otherRoles = (
    ['admin', 'support', 'read_only'] as TeamMember['role'][]
  ).filter((r) => r !== member.role);

  return (
    <Menus>
      <Menus.Toggle
        id={id}
        className="text-muted hover:text-primary flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-white/6"
      >
        <EllipsisVertical size={15} />
      </Menus.Toggle>

      <Menus.List id={id}>
        {otherRoles.map((r) => (
          <Menus.Button key={r} onClick={() => onRoleChange(r)}>
            Make {ROLE_LABEL[r].toLowerCase()}
          </Menus.Button>
        ))}
        <Menus.Button
          onClick={onRemove}
          className="text-danger flex w-full cursor-pointer items-center gap-3 rounded-sm px-4 py-2 text-left text-sm transition-colors hover:bg-white/6"
        >
          Remove member
        </Menus.Button>
      </Menus.List>
    </Menus>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <tr key={i} className="border-b border-white/6 last:border-0">
          {Array.from({ length: 5 }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 w-24 animate-pulse rounded bg-white/8" />
            </td>
          ))}
          <td className="px-4 py-3" />
        </tr>
      ))}
    </>
  );
}

function TeamPage() {
  const isAdmin = useAuthStore((s) => s.role) === 'admin';
  const { data: members = [], isLoading } = useQuery(teamQueryOptions());
  const updateRole = useUpdateMemberRole();
  const remove = useRemoveMember();

  return (
    <div className="space-y-6 p-6">
      {/* Members table Desktop */}
      <div className="bg-bg-elevated border-primary/8 hidden overflow-hidden rounded-lg border md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/8">
              <th className="text-subtle px-4 py-2 text-left text-xs uppercase">
                Member
              </th>

              <th className="text-subtle hidden px-4 py-2 text-left text-xs uppercase lg:table-cell">
                Email
              </th>

              <th className="text-subtle px-4 py-2 text-left text-xs uppercase">
                Role
              </th>

              <th className="text-subtle px-4 py-2 text-left text-xs uppercase">
                Last active
              </th>

              <th className="text-subtle px-4 py-2 text-left text-xs uppercase">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : members.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted px-4 py-10 text-center">
                  No team members yet
                </td>
              </tr>
            ) : (
              members.map((m) => {
                const status = memberStatus(m);
                return (
                  <tr
                    key={m.id}
                    className="border-b border-white/6 transition-colors last:border-0 hover:bg-white/3"
                  >
                    <td className="text-primary px-4 py-3 text-xs">
                      {m.email?.split('@')[0] ?? '—'}
                    </td>
                    <td className="text-primary hidden px-4 py-3 lg:table-cell">
                      {m.email}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          'text-accent bg-accent/12 rounded px-2 py-0.5 text-[0.625rem]',
                          ROLE_STYLE[m.role],
                        ].join(' ')}
                      >
                        {ROLE_LABEL[m.role]}
                      </span>
                    </td>
                    <td className="text-primary px-4 py-3 text-xs">
                      {formatRelativeTime(m.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          'rounded border px-2 py-0.5 text-[0.625rem] capitalize',
                          STATUS_STYLE[status],
                        ].join(' ')}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin && (
                        <ActionsMenu
                          id={m.id}
                          member={m}
                          onRoleChange={(role) =>
                            updateRole.mutate({ id: m.id, role })
                          }
                          onRemove={() => remove.mutate(m.id)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Members table Mobile  */}
      <div className="overflow-hidden rounded-lg md:hidden">
        {members.length === 0 ? (
          <p className="text-muted px-4 py-10 text-center">
            No team members yet
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {members.map((member) => {
              const status = memberStatus(member);

              return (
                <li className="bg-bg-elevated space-y-1 px-4 py-4">
                  <div className="flex justify-between">
                    <div className="flex gap-2">
                      <p className="text-primary text-sm font-medium">
                        {member.id.slice(0, 18) + ' ...'}
                      </p>
                      <span
                        className={[
                          'text-accent bg-accent/12 rounded px-2 py-0.5 text-[0.625rem]',
                          ROLE_STYLE[member.role],
                        ].join(' ')}
                      >
                        {ROLE_LABEL[member.role]}
                      </span>
                    </div>

                    <span
                      className={[
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        STATUS_DOT[status] ?? 'bg-white/40',
                      ].join(' ')}
                    />
                  </div>
                  <p className="text-subtle text-[0.625rem]">{member.email}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Permission matrix */}
      <div className="bg-bg-elevated border-primary/8 overflow-hidden rounded-lg border px-4">
        <div className="py-6">
          <p className="text-subtle text-xs uppercase">
            Role permission matrix
          </p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/8">
              <th className="text-subtle py-3 text-left text-xs uppercase">
                Permission
              </th>

              {/* Desktop Table Headers */}
              <th className="text-subtle hidden py-3 text-center text-xs uppercase md:table-cell">
                Read-only
              </th>
              <th className="text-subtle hidden py-3 text-center text-xs uppercase md:table-cell">
                Support
              </th>
              <th className="text-subtle hidden py-3 text-center text-xs uppercase md:table-cell">
                Admin
              </th>

              {/* Mobile Table Headers */}
              <th className="text-subtle py-3 text-center text-xs uppercase md:hidden">
                R/0
              </th>
              <th className="text-subtle py-3 text-center text-xs uppercase md:hidden">
                SUP
              </th>
              <th className="text-subtle py-3 text-center text-xs uppercase md:hidden">
                Admin
              </th>
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS.map((p) => (
              <tr
                key={p.label}
                className="border-b border-white/6 last:border-0"
              >
                <td className="text-muted py-3 text-xs">{p.label}</td>
                {[p.readOnly, p.support, p.admin].map((allowed, i) => (
                  <td key={i} className="px-4 py-3 text-center">
                    {allowed && (
                      <Check size={14} className="text-muted mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
